import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, apiErrorMessage } from "@/api/client";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import type { SetupJobView } from "./hooks/useAppBindings";

/** Recovery never starts a job or treats an operator confirmation as success. */
export function SetupInterruptionRecovery({ job: suppliedJob }: { job?: SetupJobView }) {
  const { user } = useAuth();
  const customerId = user?.serviceSubscription?.customerId;
  const admin = user?.currentMembership?.role === "customer_admin";
  const [job, setJob] = useState<SetupJobView | null>(suppliedJob ?? null);
  const [stopped, setStopped] = useState(false);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  useEffect(() => { setStopped(false); setChecked(false); setResolved(false); }, [job?.jobId, customerId]);
  useEffect(() => {
    if (suppliedJob) { setJob(suppliedJob); return; }
    setJob(null);
    let disposed = false;
    apiClient<{ job: SetupJobView | null }>("/console-bff/setup-jobs/current")
      .then(result => { if (!disposed) setJob(result.job); })
      .catch(() => { if (!disposed) setError("セットアップ状態を確認できません。一覧の閲覧は続けられます。"); });
    return () => { disposed = true; };
  }, [suppliedJob, customerId]);
  async function refresh() {
    setBusy(true); setError(null);
    try {
      const result = await apiClient<{ job: SetupJobView | null }>("/console-bff/setup-jobs/current");
      setJob(result.job);
    } catch (err) { setError(apiErrorMessage(err)); }
    finally { setBusy(false); }
  }
  async function resolve() {
    if (!job) return;
    setBusy(true); setError(null);
    try {
      const result = await apiClient<SetupJobView>(`/console-bff/setup-jobs/${job.jobId}/resolve`, {
        method: "POST", body: JSON.stringify({ executionStopped: stopped, externalStateChecked: checked }),
      });
      setJob(result); setResolved(true);
    } catch (err) { setError(apiErrorMessage(err)); }
    finally { setBusy(false); }
  }
  if (!job && !error) return null;
  return <section className="mb-4 space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4" aria-label="セットアップの状態確認">
    {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}
    {resolved ? <>
      <p role="status">処理結果を確定しました。一覧に対象アプリがある場合は設定から修復し、未登録の場合はアプリを追加してください。</p>
      <Button asChild variant="outline"><Link to="/console/app-bindings">アプリ一覧へ戻る</Link></Button>
    </> : job?.status === "reconciliation_required" ? <>
      <h2 className="font-semibold">中断したセットアップの確認が必要です（アプリID: {job.kintoneAppId}）</h2>
      <p className="text-sm">一覧の閲覧は続けられます。kintone側に変更が残っている可能性があるため、期限切れだけでは再実行しません。</p>
      {admin ? <details>
        <summary className="cursor-pointer font-medium">管理者による中断確定</summary>
        <div className="mt-3 space-y-3 text-sm">
          <p>実行環境の管理者に旧セットアップ処理とブラウザの停止を確認し、kintoneのプラグイン・APIトークン・未公開設定・アプリ更新結果を確認してください。確認できない場合は確定しないでください。</p>
          <label className="flex items-start gap-2"><input type="checkbox" checked={stopped} disabled={busy} onChange={e => setStopped(e.target.checked)} />旧実行元とブラウザの停止を確認した</label>
          <label className="flex items-start gap-2"><input type="checkbox" checked={checked} disabled={busy} onChange={e => setChecked(e.target.checked)} />kintone側の変更内容と更新結果を確認し、既存設定から修復できると判断した</label>
          <p>この操作は中断を記録します。kintone側の変更を取り消したり、セットアップを自動再実行したりしません。</p>
          <Button type="button" disabled={busy || !stopped || !checked} onClick={() => void resolve()}>中断を確定する</Button>
        </div>
      </details> : <p className="text-sm">組織管理者に中断の確認を依頼してください。</p>}
    </> : job ? <p className="text-sm">セットアップの受付があります（アプリID: {job.kintoneAppId}）。一覧は引き続き閲覧できます。</p> : null}
    {!resolved ? <Button type="button" variant="outline" disabled={busy} onClick={() => void refresh()}>セットアップ状態を再確認</Button> : null}
  </section>;
}
